import { casOpsApi, CollectionModel, QueryTag, TagList } from '../api';
import { ModelWithId } from './commonModels';
import { UserRole } from './settingsApi';

export interface UserUnionModel extends ModelWithId {
  un: string; // user name
  fn: string; // first name
  ln: string; // last name
  em: string; // email
  locked: boolean; //whether a user is locked out or not
  ro: boolean; // read only. For list view, it means delete is disabled. For single view, it means roles cannot be modified.
  roles: UserRole[]; // List of user roles. An empty list is equivalent to a standard user with read-only access.
}

export interface UserBasicModel extends Omit<UserUnionModel, 'roles'> {}

export interface UserCreateModel extends Pick<UserUnionModel, 'un' | 'fn' | 'ln' | 'em' | 'roles'> {
  pa: string; // password
}

export interface UserUpdateModel extends Pick<UserUnionModel, 'fn' | 'ln' | 'em' | 'roles'> {}

export interface UserFormModel extends UserCreateModel, UserUpdateModel {
  cp: string; // confirm password
  ro: boolean; // readonly roles
}

export const usersApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    getUsers: build.query<UserBasicModel[], void>({
      query: () => 'api/users',
      transformResponse: (response: CollectionModel<UserBasicModel>, meta, arg) => response.items,
      providesTags: (result, error, arg) => (result ? [{ type: QueryTag.User, id: TagList }] : []),
    }),
    getUserById: build.query<UserUnionModel, ModelWithId>({
      query: ({ id }) => `api/users/${id}`,
      providesTags: (result, error, { id }) => (result ? [{ type: QueryTag.User, id: id }] : []),
    }),
    addUser: build.mutation<void, UserCreateModel>({
      query: body => ({
        url: `api/users`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: QueryTag.User, id: TagList }],
    }),
    editUser: build.mutation<void, UserUpdateModel & ModelWithId>({
      query: ({ id, ...body }) => ({
        url: `api/users/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              QueryTag.SettingsPersonnelSafety,
              { type: QueryTag.User, id: TagList },
              { type: QueryTag.User, id: id },
            ]
          : [],
    }),
    removeUser: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              QueryTag.SettingsPersonnelSafety,
              { type: QueryTag.User, id: TagList },
              { type: QueryTag.User, id: id },
            ]
          : [],
    }),
    unlockUser: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/users/${id}/unlock`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.User, id: TagList },
              { type: QueryTag.User, id: id },
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useAddUserMutation,
  useEditUserMutation,
  useRemoveUserMutation,
  useUnlockUserMutation,
} = usersApi;
